// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XAvatar from "@bg/App/Components/XAvatar/XAvatar";
import setHeaderSignInDrawerOpen from "@bg/App/Handlers/Components/xHeader/xHeaderHandler";
import { xHeaderActions, xHeaderState } from "@bg/App/Store/Reducers";
import Button from "@bg/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import Link from "@bg/MobiusOverrides/Link/Link";
import { screen } from "@bg/XDL/Helpers";
import ArrowRight from "@bg/XDL/Icons/ArrowRight";
import { styledScrollbar } from "@bg/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { MappedLink } from "@insite/mobius/Menu";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    links = "links",
    benefits = "benefits",
    isGroup = "isGroup",
    benefitsTitle = "benefitsTitle",
}

export interface CustomLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        linkTitle: string;
        title: string;
    };
}
export interface CustomBenefitModel {
    fields: {
        benefit: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        [fields.links]: CustomLinkModel[];
        [fields.benefits]: CustomBenefitModel[];
        [fields.benefitsTitle]: string;
    };
}

const getAllowedLinks = (state: ApplicationState, ownProps: OwnProps) => {
    const allowedLinksFromOOtb = getPageLinkByPageType(state, "MyAccountPage")?.children;
    const links = (ownProps.fields.links || [])
        .map((linkItem: CustomLinkModel) => {
            const node = getPageLinkByNodeId(state, linkItem?.fields?.destination?.value);
            const url = node?.url || linkItem?.fields?.destination?.value;
            // validate custom urls
            const excludeFromNavigationCustomLink = allowedLinksFromOOtb?.find(item => {
                if (item.url.toLocaleLowerCase() === url.toLocaleLowerCase()) {
                    return item.excludeFromNavigation;
                }
                return null;
            });

            return {
                isGroup: linkItem.fields.isGroup,
                url: node?.url || linkItem?.fields?.destination?.value,
                excludeFromNavigation: node?.excludeFromNavigation || excludeFromNavigationCustomLink,
                title: linkItem.fields.isGroup ? linkItem.fields.title : node?.title,
                pageType: node?.type,
                linkTitle: linkItem.fields.linkTitle,
            };
        })
        .filter(
            i =>
                ((!i.excludeFromNavigation || i.isGroup) && i.pageType !== "ChangeCustomerPage") ||
                (i.pageType === "ChangeCustomerPage" && state.context.session.displayChangeCustomerLink),
        );

    return links;
};

const mapStateToProps = (state: ApplicationState & xHeaderState, ownProps: OwnProps) => {
    const firstName = state.context.session.firstName;
    const lastName = state.context.session.lastName;
    const userName = state.context.session.userName;
    const primarySalesperson = JSON.parse(state.context.session?.properties?.primarySalesPerson || "{}");
    const benefits =
        ownProps.fields.benefits &&
        ownProps.fields.benefits.length > 0 &&
        ownProps.fields.benefits.map((item: CustomBenefitModel) => ({
            title: item.fields.benefit,
        }));
    const { search } = getLocation(state);
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        isLoading: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
        userName,
        firstName,
        lastName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // customization
        benefits,
        isSticky: state.xHeader.isSticky,
        session: state.context.session,
        selectedBillToId: state.context.session.billToId,
        billToState: getBillToState(state, state.context.session.billToId),
        links: getAllowedLinks(state, ownProps),
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        helloUser: firstName || userName,
        primarySalesperson,
        headerSignInDrawerOpen: state.xHeader.headerSignInDrawerOpen,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // customization
    loadBillTo,
    setHeaderSignInDrawerOpen,
    setNavDrawerIsOpen,
};

export interface HeaderSignInStyles {
    myAccountGroup?: InjectableCss;
    myAccountLink?: InjectableCss;
    myAccountBodyWrapper?: InjectableCss;
    myAccountBody?: InjectableCss;
    myAccountHeaderWrapper?: InjectableCss;
    myAccountHeader?: InjectableCss;
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleClickableSignIn?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    myAccountContainer: InjectableCss;
    drawer?: DrawerPresentationProps;
    myAccountDrawer?: DrawerPresentationProps;
    tabGroupWrapper?: TabGroupPresentationProps;
    salesRepWrapper?: InjectableCss;
    salesRepImgWrapper?: InjectableCss;
    salesRepImg?: LazyImageProps;
    salesRepInfo?: InjectableCss;
    salesRepLink?: InjectableCss;
    salesRepPhone?: InjectableCss;
    myAccountFooter?: InjectableCss;
    eyeIcon?: IconPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    titleClickable: {
        css: css`
            display: flex;
            flex-direction: column;
            &:hover {
                span {
                    color: ${getColor("text.link")};
                    svg {
                        fill: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    titleClickableSignIn: {
        css: css`
            display: flex;
            flex-direction: column;
            &:hover {
                span {
                    color: ${getColor("text.link")};
                    svg {
                        fill: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    titleIcon: {
        color: "text.main",
        css: css`
            ${(props: any) => props.sticky && `margin-top: 3px;`}
        `,
    },
    titleTypography: {
        css: css`
            font-size: 13px;
            line-height: 16px;
            margin-top: 6px;
            color: ${getColor("common.white")};
            ${screen("lte", "md")`display: none;`}
            ${(props: any) => props.sticky && `display: none;`}
        `,
    },
    myAccountLink: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui3")};
            padding: 15px 0 15px 0;
            width: 100%;
            span {
                color: ${getColor("common.primary2")} !important;
                display: flex;
                align-items: center;
            }

            &&:last-child {
                border-bottom: 0;
            }
        `,
    },
    // customization
    myAccountContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            height: 100%;
        `,
    },
    drawer: {
        cssOverrides: {
            drawerContent: css`
                // removing cut-off on signin button focus
                padding: 0 38px 20px;
                overflow-y: hidden;
                .sign-in-tabs {
                    li:first-child {
                        margin-right: 15px;
                    }
                    li {
                        height: 33px;
                        margin-bottom: 5px;
                    }
                }
            `,
        },
    },
    myAccountDrawer: {
        cssOverrides: {
            drawerContent: css`
                padding: 0;
                overflow-y: hidden;
            `,
        },
    },
    tabGroupWrapper: {
        cssOverrides: {
            tabContent: css`
                padding: 0;
                margin: 0;
                border: 0;
                margin-top: 34px;
                .sign-in-wrapper {
                    padding: 0 !important;
                }
            `,
        },
    },
    myAccountHeaderWrapper: {
        css: css`
            background: ${getColor("common.ui4")};
            padding-bottom: 4px;
        `,
    },
    myAccountHeader: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: ${getColor("common.white")} !important;
            border-bottom: 1px solid ${getColor("common.ui3")};
            padding-left: 37px;
            padding-bottom: 30px;
            padding-right: 37px;
            button {
                background: inherit !important;
                text-transform: capitalize !important;
                padding: 0 !important;
                color: ${getColor("common.primary2")} !important;
                &:hover {
                    color: ${getColor("common.primary2")} !important;
                }
                svg {
                    width: 17px;
                    color: ${getColor("primary")};
                    margin-left: 2px;
                }
            }

            ${screen("lte", "xs")`padding-left: 18px;padding-right: 18px;padding-bottom: 25px;`}
        `,
    },
    myAccountBodyWrapper: {
        css: css`
            background: ${getColor("common.ui4")};
            padding-bottom: 4px;
            overflow: hidden;
        `,
    },
    myAccountBody: {
        css: css`
            padding: 0 38px;
            background: ${getColor("common.white")} !important;
            border-bottom: 1px solid ${getColor("common.ui3")};
            height: 100%;
            overflow-y: auto;
            ${styledScrollbar(5)}

            ${screen("lte", "xs")`padding: 0 18px;`}
        `,
    },
    salesRepImgWrapper: {
        css: css`
            display: flex;
            margin-top: 9px;
        `,
    },
    salesRepWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            background: ${getColor("common.white")};
            padding: 15px 38px;
            border-bottom: 1px solid ${getColor("common.ui3")};
            > div {
                width: 100%;
            }
        `,
    },
    salesRepImg: {
        cloudinary: false,
        css: css`
            border-radius: 50%;
            border: 1px solid ${getColor("primary")};
            width: 55px;
            height: 55px;
        `,
        disableLazyLoad: true,
    },
    salesRepInfo: {
        css: css`
            margin-left: 13px;
            flex-direction: column;
            display: flex;
        `,
    },
    salesRepLink: {
        css: css`
            span {
                font-weight: 500;
                color: ${getColor("common.primary2")};
                word-break: break-all;
            }
        `,
    },
    salesRepPhone: {
        css: css`
            font-weight: 500;
            color: ${getColor("common.primary2")};
        `,
    },
    myAccountFooter: {
        css: css`
            padding: 31px 0;
            display: flex;
            justify-content: center;
            button {
                max-width: 317px;
            }
        `,
    },
    eyeIcon: {
        size: 16,
        css: css`
            margin-right: 4px;
        `,
    }
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

const styles = headerSignInStyles;
let icon: React.ComponentType;

const HeaderSignIn = ({
    userName,
    firstName,
    lastName,
    id,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    isPunchOutSession,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // custom states
    isLoading,
    isSticky,
    links,
    session,
    selectedBillToId,
    billToState,
    changeCustomerUrl,
    helloUser,
    primarySalesperson,
    // custom dispatchers
    loadBillTo,
    setHeaderSignInDrawerOpen,
    headerSignInDrawerOpen,
    navDrawerIsOpen,
    setNavDrawerIsOpen,
    displayChangeCustomerLink,
    myAccountPageLink,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const [storeLoaded, setStoreLoaded] = React.useState(false);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    // if (!myAccountPageLink) {
    //     return null;
    // }
    
    const { includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    // Xng-Change not used
    // const showIcon = visibilityState === "both" || visibilityState === "icon";
    // const showLabel = visibilityState === "both" || visibilityState === "label";

    const handleCloseModal = () => {
        setHeaderSignInDrawerOpen({ open: false });
    };

    const currentMode = getCookie(NavigationModeCookieName);
    // let menuItems = (myAccountPageLink?.children?.filter(
    //     pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    // ) ?? []) as MappedLink[];

    // Xng-Change
    const getMenuItem = (title: "Inventory" | "Pricing") => {
        // return {
        //     title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
        //     iconProps: {
        //         src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
        //         size: 22,
        //         css: css`
        //             margin-left: 20px;
        //             margin-right: -12px;
        //         `,
        //     },
        //     clickableProps: {
        //         css: css`
        //             display: flex;
        //             text-transform: uppercase;
        //         `,
        //         onClick: () => {
        //             title === "Inventory"
        //                 ? setShowInventory({ showInventory: !showInventory })
        //                 : setShowPricing({ showPricing: !showPricing });
        //         },
        //     },
        // };
        return (
            <Link
                key={uniqueId()}
                {...styles.myAccountLink}
                onClick={() => {
                title === "Inventory"
                    ? setShowInventory({ showInventory: !showInventory })
                    : setShowPricing({ showPricing: !showPricing });
            }}>
                <Icon
                    src={(title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye}
                    {...styles.eyeIcon}
                    ></Icon>
                {title === "Inventory" ? translate("Inventory") : translate("Pricing")}
            </Link>
        )
    };

    // if (!currentMode || currentMode === "Storefront") {
    //     menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    //     showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
    //     showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    // }

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    React.useEffect(() => {
        // load current billTo
        if (!billToState.value && !billToState.isLoading && selectedBillToId && session.isAuthenticated) {
            loadBillTo({ billToId: selectedBillToId });
        }
        let signInStatusText = translate("Sign In / Register");
        if (userName && !currentUserIsGuest) {
            signInStatusText = userName;
        }
        setStoreLoaded(true);
    }, []);

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const toggleDrawer = () => {
        if (storeLoaded) {
            if (navDrawerIsOpen && !headerSignInDrawerOpen) {
                setNavDrawerIsOpen({ navDrawerIsOpen: false });
            }
            setHeaderSignInDrawerOpen({ open: !headerSignInDrawerOpen });
        }
    };

    const SignInElement = () => (
        <StyledWrapper>
            <Clickable
                {...styles.titleClickableSignIn}
                onClick={toggleDrawer}
                data-test-selector="header_signIn"
                className="header_signIn"
            >
                <Icon
                    {...styles.titleIcon}
                    size={isSticky ? 30 : 24}
                    src="Account"
                    color="common.white"
                    {...({ sticky: isSticky } as any)}
                />
                <Typography {...styles.titleTypography} {...({ sticky: isSticky } as any)}>
                    {translate("Welcome!")}
                </Typography>
            </Clickable>
        </StyledWrapper>
    );

    const UserElement = () => (
        <Clickable {...styles.titleClickable} data-test-selector="header_signIn" onClick={toggleDrawer}>
            <Hidden above="md">
                <Icon
                    {...styles.titleIcon}
                    size={isSticky ? 30 : 24}
                    src="Account"
                    color="common.white"
                    {...({ sticky: isSticky } as any)}
                />
            </Hidden>
            <Hidden below="lg">
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        background: ${getColor("common.white")};
                        width: 46px;
                        height: 46px;

                        * {
                            font-size: 22px;
                            letter-spacing: 0;
                            line-height: 16px;
                            text-transform: uppercase;
                            color: ${getColor("text.main")};
                        }
                    `}
                >
                    {firstName && lastName ? (
                        <Typography {...({ sticky: isSticky } as any)} data-test-selector="header_userName">
                            {firstName && firstName.charAt(0)}
                            {lastName && lastName.charAt(0)}
                        </Typography>
                    ) : (
                        <Typography {...({ sticky: isSticky } as any)} data-test-selector="header_userName">
                            {userName && userName.charAt(0)}
                            {userName && userName.length > 1 && userName.charAt(1)}
                        </Typography>
                    )}
                </StyledWrapper>
            </Hidden>
        </Clickable>
    );
    const MyAccountOverlay = () => (
        <StyledWrapper {...styles.myAccountContainer}>
            {/* Heading */}
            <StyledWrapper {...styles.myAccountHeaderWrapper}>
                <StyledWrapper {...styles.myAccountHeader}>
                    <StyledWrapper
                        css={css`
                            width: calc(100% - 90px);
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        `}
                    >
                        {myAccountPageLink ? (
                            <Link href={myAccountPageLink.url} onClick={handleCloseModal}>
                                <Typography
                                    size="28px"
                                    lineHeight="22px"
                                    weight="bold"
                                    ellipsis={true}
                                    as="p"
                                    css={css`
                                        text-overflow: ellipsis;
                                        max-width: 240px;
                                        padding-bottom: 5px;
                                        color: ${getColor("text.main")} !important;
                                        text-transform: capitalize;
                                        @media (max-width: 769px) {
                                            max-width: 600px;
                                        }
                                        @media (max-width: 501px) {
                                            max-width: 264px;
                                        }
                                        @media (max-width: 350px) {
                                            white-space: pre-wrap;
                                        }
                                    `}
                                >
                                    {`${translate("Hello")} ${helloUser.toLowerCase()}!`}
                                </Typography>
                            </Link>
                        ) : (
                            <Typography
                                size="28px"
                                lineHeight="22px"
                                weight="bold"
                                css={css`
                                    text-overflow: ellipsis;
                                    text-transform: capitalize;
                                `}
                            >
                                {`${translate("Hello")},  ${helloUser?.toLowerCase()}!`}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper>
                        <Button
                            css={css`
                                &&:focus,
                                &&:hover {
                                    box-shadow: none;
                                    text-decoration: underline;
                                }
                            `}
                            aria-label={translate("sign Out")}
                            onClick={onSignOutHandler}
                            data-test-selector="sign-Out-link"
                        >
                            <Typography as="span">{translate("sign Out")}</Typography>
                            <ArrowRight />
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* Body */}
            <StyledWrapper {...styles.myAccountBodyWrapper}>
                <StyledWrapper {...styles.myAccountBody}>
                    {links.map((link: any) =>
                        link?.isGroup ? (
                            "" // TODO: will decide about this once we got confirmation about group title
                        ) : (
                            <Link key={uniqueId()} href={link.url} {...styles.myAccountLink} onClick={handleCloseModal}>
                                {link.linkTitle || link.title}
                            </Link>
                        ),
                    )}
                    {(!currentMode || currentMode === "Storefront") &&
                        <>
                            {showInventoryToggle && displayPricingAndInventory && getMenuItem("Inventory")}
                            {showPricingToggle && displayPricingAndInventory && getMenuItem("Pricing")}
                        </>
                    }
                </StyledWrapper>
            </StyledWrapper>

            {/* Sale Rep  */}
            {primarySalesperson && primarySalesperson?.Name && (
                <StyledWrapper {...styles.myAccountHeaderWrapper}>
                    <StyledWrapper {...styles.salesRepWrapper}>
                        <Typography as="span" size="18px" lineHeight="20px" weight="bold">
                            {translate("Your Sales Representative")}
                        </Typography>
                        <StyledWrapper {...styles.salesRepImgWrapper}>
                            <StyledWrapper>
                                {primarySalesperson?.Image ? (
                                    <LazyImage
                                        src={primarySalesperson.Image}
                                        altText={primarySalesperson.Name}
                                        {...styles.salesRepImg}
                                    ></LazyImage>
                                ) : (
                                    <XAvatar
                                        firstName={primarySalesperson.UserProfileFirstName || primarySalesperson.Name}
                                        lastName={primarySalesperson.UserProfileLastName || ""}
                                        width="55"
                                        height="55"
                                    />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.salesRepInfo}>
                                {primarySalesperson?.Name && (
                                    <Typography
                                        css={css`
                                            font-weight: 500;
                                        `}
                                    >
                                        {primarySalesperson.Name}
                                    </Typography>
                                )}
                                {primarySalesperson?.Email && (
                                    <Link {...styles.salesRepLink} href={`mailto: ${primarySalesperson.Email}`}>
                                        {primarySalesperson.Email}
                                    </Link>
                                )}

                                {primarySalesperson?.Phone && (
                                    <a href={`tel:${primarySalesperson.Phone}`}>
                                        <Typography as="span" {...styles.salesRepPhone}>
                                            {primarySalesperson.Phone}
                                        </Typography>
                                    </a>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {/* footer */}
            {displayChangeCustomerLink && (
                <StyledWrapper {...styles.myAccountFooter}>
                    <Button
                        color="primary"
                        css={css`
                            width: 100%;
                        `}
                        href={changeCustomerUrl}
                        onClick={handleCloseModal}
                    >
                        {translate("Change Ship-To")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <>
                    <UserElement />

                    <Drawer
                        draggable={false}
                        position="right"
                        {...styles.myAccountDrawer}
                        isOpen={headerSignInDrawerOpen}
                        handleClose={handleCloseModal}
                        contentLabel="account menu drawer"
                    >
                        <MyAccountOverlay />
                    </Drawer>
                </>
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <>
                    <SignInElement />

                    <Drawer
                        draggable={false}
                        position="right"
                        {...styles.drawer}
                        isOpen={headerSignInDrawerOpen}
                        handleClose={handleCloseModal}
                        contentLabel="account menu drawer"
                    >
                        {/* <XngSignIn /> */}
                        <TabGroup {...styles.tabGroupWrapper} className="sign-in-tabs" current="SignInComponent">
                            <Tab
                                headline={translate("Sign In")}
                                typographyProps={{
                                    size: "28px",
                                    lineHeight: "22px",
                                }}
                                tabKey="SignInComponent"
                            >
                                <LoadingOverlay loading={isLoading}>
                                    <Zone contentId={id} zoneName="SignInTabContent" />
                                </LoadingOverlay>
                            </Tab>
                            <Tab
                                headline={translate("Register")}
                                typographyProps={{
                                    size: "28px",
                                    lineHeight: "22px",
                                }}
                                tabKey="Register"
                            >
                                <Zone contentId={id} zoneName="RegisterTabContent" />
                            </Tab>
                        </TabGroup>
                    </Drawer>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In / MyAccount Links",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.benefitsTitle,
                displayName: "Benefits Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (item.fields[fields.isGroup]) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: fields.isGroup,
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "title",
                        displayName: "Groupe Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[fields.isGroup] === true,
                    },
                ],
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
